import React, { useState, useEffect } from 'react';
import { Mail, RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, ChevronLeft, ChevronRight, Eye, RotateCcw } from 'lucide-react';
import api from '../../api/api';

const EmailLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/communication/email-logs?page=${page}&limit=15`);
      setLogs(response.data.logs || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'sending':
      case 'retrying':
        return <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      sending: 'bg-blue-100 text-blue-800',
      retrying: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRetry = async (logId) => {
    setRetrying(true);
    try {
      await api.post(`/communication/email-logs/${logId}/retry`);
      alert('Retry started for failed emails!');
      fetchLogs();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to retry');
    } finally {
      setRetrying(false);
    }
  };

  const viewLogDetails = async (logId) => {
    try {
      const response = await api.get(`/communication/email-logs/${logId}`);
      setSelectedLog(response.data);
    } catch (error) {
      console.error('Error fetching log details:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Logs</h1>
          <p className="text-sm text-gray-500 mt-1">Track all sent emails and their delivery status</p>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="h-12 w-12 mx-auto text-gray-300" />
            <p className="mt-4 text-gray-500">No email logs found</p>
            <p className="text-sm text-gray-400 mt-1">Sent emails will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{log.subject}</p>
                      <p className="text-xs text-gray-500 capitalize">{log.recipient_type}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {log.recipient_count}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-green-600">{log.sent_count || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-red-600">{log.failed_count || 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(log.status)}
                        {getStatusBadge(log.status)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewLogDetails(log.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                        {log.failed_count > 0 && log.status !== 'retrying' && (
                          <button
                            onClick={() => handleRetry(log.id)}
                            disabled={retrying}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                            title="Retry Failed"
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Email Log Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusIcon(selectedLog.status)}
                    {getStatusBadge(selectedLog.status)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(selectedLog.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Recipient Type</p>
                  <p className="font-medium capitalize">{selectedLog.recipient_type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sent By</p>
                  <p className="font-medium">{selectedLog.sender_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Recipients</p>
                  <p className="font-medium">{selectedLog.recipient_count}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Sent / Failed</p>
                  <p className="font-medium">
                    <span className="text-green-600">{selectedLog.sent_count}</span>
                    {' / '}
                    <span className="text-red-600">{selectedLog.failed_count}</span>
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-medium mt-1">{selectedLog.subject}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Message</p>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap">
                  {selectedLog.message}
                </div>
              </div>

              {selectedLog.recipients_preview && selectedLog.recipients_preview.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Recipients Preview (first 10)</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedLog.recipients_preview.map((email, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs">{email}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedLog.failed_emails && selectedLog.failed_emails.length > 0 && (
                <div>
                  <p className="text-sm text-red-600 font-medium">Failed Emails ({selectedLog.failed_emails.length})</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {selectedLog.failed_emails.map((email, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs">{email}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              {selectedLog.failed_count > 0 && selectedLog.status !== 'retrying' && (
                <button
                  onClick={() => {
                    handleRetry(selectedLog.id);
                    setSelectedLog(null);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  <RotateCcw size={16} />
                  Retry Failed
                </button>
              )}
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLogs;
